import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Message } from '../types';
import { generateSoulResponseStream, generateRoutineWelcomeMessageStream } from '../utils/openai';
import ReactMarkdown from 'react-markdown';

import BackgroundMusicService from '../services/BackgroundMusicService';

const ChatPage: React.FC = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [streamedResponse, setStreamedResponse] = useState('');
  const [showActivities, setShowActivities] = useState(false);
  const [detectedEmotion, setDetectedEmotion] = useState<string>('');
  const [routineInfo, setRoutineInfo] = useState<any>(null);
  const [backgroundMusicService] = useState(() => new BackgroundMusicService());
  const [isInitialized, setIsInitialized] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initializationRef = useRef(false);

  // 다국어 지원 액티비티 목록 생성
  const getActivities = () => [
    {
      id: 'meditation',
      name: t('activity.meditation.name'),
      description: t('activity.meditation.description'),
      icon: '🧘',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      id: 'breathing',
      name: t('activity.breathing.name'),
      description: t('activity.breathing.description'),
      icon: '🌬️',
      color: 'bg-green-100 text-green-600'
    },
    {
      id: 'piano',
      name: t('activity.piano.name'),
      description: t('activity.piano.description'),
      icon: '🎹',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      id: 'sketch',
      name: t('activity.sketch.name'),
      description: t('activity.sketch.description'),
      icon: '🎨',
      color: 'bg-orange-100 text-orange-600'
    },
    {
      id: 'voice',
      name: t('activity.voice.name'),
      description: t('activity.voice.description'),
      icon: '🎤',
      color: 'bg-pink-100 text-pink-600'
    },
    {
      id: 'writing',
      name: t('activity.writing.name'),
      description: t('activity.writing.description'),
      icon: '✍️',
      color: 'bg-yellow-100 text-yellow-600'
    }
  ];

  // 루틴 정보 가져오기 및 배경음악 처리
  useEffect(() => {
    // location.state가 없으면 리턴
    if (!location.state) return;
    
    const initializeRoutine = async () => {
      // 이미 초기화되었으면 리턴
      if (isInitialized || initializationRef.current) return;
      
      console.log('ChatPage 초기화 시작');
      initializationRef.current = true;
      setRoutineInfo(location.state);
      setIsInitialized(true);
      
      // 기존 배경음악 정지 후 새로운 배경음악 재생
      // console.log('배경음악 처리 시작');
      // await backgroundMusicService.stopBackgroundMusic();
      // if (location.state.backgroundMusic) {
      //   console.log(`새로운 배경음악 재생: ${location.state.backgroundMusic}`);
      //   await backgroundMusicService.playBackgroundMusic(location.state.backgroundMusic);
      // }
      
      // 루틴 시작 메시지 자동 생성 (프롬프트 사용)
      if (location.state.routineTitle) {
        console.log('루틴 환영 메시지 생성 시작');
        setIsTyping(true);
        setStreamedResponse('');
        
        // 환영 메시지를 위한 임시 메시지 추가
        const tempWelcomeMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: '',
          timestamp: new Date().toISOString()
        };

        setMessages([tempWelcomeMessage]);
        
        try {
          let fullResponse = '';
          await generateRoutineWelcomeMessageStream(
            location.state.routineTitle,
            location.state.routineDescription,
            language,
            (chunk) => {
              fullResponse += chunk;
              const { cleanedContent } = detectEmotion(fullResponse);
              setStreamedResponse(cleanedContent);
              // 임시 메시지 업데이트 (마커 제거된 내용 표시)
              setMessages(prev => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage && lastMessage.role === 'assistant') {
                  lastMessage.content = cleanedContent;
                }
                return newMessages;
              });
            }
          );

          // 최종 메시지 업데이트
          setMessages(prev => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.role === 'assistant') {
              const { cleanedContent } = detectEmotion(fullResponse);
              lastMessage.content = cleanedContent;
            }
            return newMessages;
          });

          setStreamedResponse('');
          console.log('루틴 환영 메시지 생성 완료');
        } catch (error) {
          console.error('루틴 환영 메시지 생성 실패:', error);
          // 실패 시 기본 메시지 사용
          const fallbackMessage: Message = {
            id: Date.now().toString(),
            role: 'assistant',
            content: `안녕하세요! ${location.state.routineTitle}을 시작해보겠습니다. ${location.state.routineDescription} 어떤 감정을 느끼고 계신가요?`,
            timestamp: new Date().toISOString()
          };
          setMessages([fallbackMessage]);
        } finally {
          setIsTyping(false);
        }
      } else {
        // 쏘울 진입 시 기본 메시지
        console.log('쏘울 진입 - 기본 메시지 생성');
        const defaultMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: t('chat.welcome'),
          timestamp: new Date().toISOString()
        };
        setMessages([defaultMessage]);
      }
    };

    initializeRoutine();
  }, [location.state?.routineTitle]); // 의존성 배열을 더 구체적으로 변경

  const scrollToBottom = () => {
    const chatContainer = document.querySelector('.overflow-y-auto');
    if (chatContainer) {
      chatContainer.scrollTop = chatContainer.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamedResponse, showActivities]);

  // 감정 감지 함수: <<감정>> 마커를 우선 추출하고, 없으면 키워드 기반으로 감지
  const detectEmotion = (
    content: string
  ): { emotion: string | null; cleanedContent: string } => {
    // 1) <<감정>> 패턴 감지 (여러 개가 있으면 마지막 것을 사용)
    const markerRegex = /<<\s*([^<>]+?)\s*>>/g;
    let markerMatch: RegExpExecArray | null;
    let lastEmotionFromMarker: string | null = null;
    while ((markerMatch = markerRegex.exec(content)) !== null) {
      lastEmotionFromMarker = markerMatch[1].trim();
    }

    if (lastEmotionFromMarker) {
      const cleaned = content.replace(markerRegex, '').replace(/\s{2,}/g, ' ').trim();
      return { emotion: lastEmotionFromMarker, cleanedContent: cleaned };
    }

    // 2) 마커가 없으면 키워드 기반 감지 (기존 로직)
    const emotionKeywords: Record<string, string[]> = {
      '기쁨': ['기쁘', '행복', '즐거', '신나', '설렘'],
      '슬픔': ['슬프', '우울', '속상', '눈물', '외로'],
      '화남': ['화나', '짜증', '분노', '열받', '스트레스'],
      '불안': ['불안', '걱정', '긴장', '두려', '떨림'],
      '평온': ['평온', '차분', '안정', '편안', '여유'],
      '피곤': ['피곤', '지치', '힘들', '무기력', '나른']
    };

    for (const [emotion, keywords] of Object.entries(emotionKeywords)) {
      if (keywords.some((keyword) => content.includes(keyword))) {
        return { emotion, cleanedContent: content };
      }
    }
    return { emotion: null, cleanedContent: content };
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isTyping) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);
    setStreamedResponse('');
    setShowActivities(false);

    // 사용자가 메시지를 보낸 후 자동 스크롤
    setTimeout(() => scrollToBottom(), 100);

    // 스트리밍 응답을 위한 임시 메시지 추가
    const tempAssistantMessage: Message = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, tempAssistantMessage]);

    try {
      let fullResponse = '';
      
      // 루틴 진입 시와 쏘울 진입 시 다른 프롬프트 사용
      const isRoutine = !!location.state?.routineTitle;
      
      await generateSoulResponseStream(
        [...messages, userMessage],
        language,
        (chunk) => {
          fullResponse += chunk;
          const { cleanedContent } = detectEmotion(fullResponse);
          setStreamedResponse(cleanedContent);
          // 임시 메시지 업데이트 (마커 제거된 내용 표시)
          setMessages(prev => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.role === 'assistant') {
              lastMessage.content = cleanedContent;
            }
            return newMessages;
          });
        },
        isRoutine
      );

      // 감정 감지 및 액티비티 표시 (루틴에서만)
      if (isRoutine) {
        const { emotion } = detectEmotion(fullResponse);
        if (emotion) {
          setDetectedEmotion(emotion);
          setShowActivities(true);
        }
      }

      // 최종 메시지 업데이트
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage && lastMessage.role === 'assistant') {
          const { cleanedContent } = detectEmotion(fullResponse);
          lastMessage.content = cleanedContent;
        }
        return newMessages;
      });

      setStreamedResponse('');
    } catch (error) {
      console.error('Error generating response:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: t('error.api'),
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
      // AI 응답 완료 후 자동 스크롤
      setTimeout(() => scrollToBottom(), 100);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleActivitySelect = async (activityId: string) => {
    // 배경음악 정지
    await backgroundMusicService.stopBackgroundMusic();
    
    // 액티비티 선택 시 해당 페이지로 이동
    window.location.href = `/activity?selected=${activityId}&emotion=${detectedEmotion}`;
  };

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      // 컴포넌트가 언마운트될 때 배경음악 정지
      backgroundMusicService.stopBackgroundMusic();
    };
  }, [backgroundMusicService]);

  return (
    <div className="w-full max-w-4xl mx-auto h-[calc(100vh-200px)] flex flex-col px-4">
      <div className="card p-4 mb-3">
        <h1 className="text-lg font-bold text-gray-900 mb-2">
          {routineInfo?.routineTitle || t('nav.chat')}
        </h1>
        <p className="text-sm text-gray-600">
          {routineInfo?.routineDescription || t('chat.ai.soul.description')}
        </p>
      </div>

      <div className="flex-1 bg-white rounded-2xl border border-grey overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && !isTyping && !routineInfo && (
            <div className="text-center text-gray-500 py-6">
              <div className="text-3xl mb-3">💬</div>
              <p className="text-sm">{t('chat.start.conversation')}</p>
            </div>
          )}

          {messages.map((message) => {
            // 빈 어시스턴트 메시지는 렌더링하지 않음 (스트리밍 시작 직후 빈 말풍선 방지)
            if (message.role === 'assistant' && (!message.content || message.content.trim() === '')) {
              return null;
            }
            return (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[70%] p-3 rounded-2xl ${
                    message.role === 'user'
                      ? 'bg-mint text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  <ReactMarkdown className="prose prose-sm max-w-none">
                    {message.content}
                  </ReactMarkdown>
                </div>
              </div>
            );
          })}

          {isTyping && streamedResponse === '' && (
            <div className="flex justify-start">
              <div className="bg-gray-100 text-gray-900 p-3 rounded-2xl">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-xs text-gray-500">{t('chat.typing')}</span>
                </div>
              </div>
            </div>
          )}

          {showActivities && (
            <div className="bg-blue-50 rounded-2xl p-5">
              <h3 className="text-base font-semibold mb-3 text-center">
                {t('chat.emotion.detected')}: {detectedEmotion}
              </h3>
              <p className="text-center text-sm text-gray-600 mb-5">
                {t('chat.emotion.expression')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {getActivities().map((activity) => (
                  <button
                    key={activity.id}
                    onClick={() => handleActivitySelect(activity.id)}
                    className="card p-3 text-center hover:shadow-lg transition-shadow"
                  >
                    <div className="text-2xl mb-2">{activity.icon}</div>
                    <h4 className="font-semibold text-sm text-gray-900 mb-1">
                      {activity.name}
                    </h4>
                    <p className="text-xs text-gray-600">
                      {activity.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="border-t border-grey p-4">
          <div className="flex space-x-3">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('chat.placeholder')}
              disabled={isTyping}
              className="flex-1 input-field text-sm"
              style={{
                fontSize: '16px', // iOS에서 자동 확대 방지
                transform: 'translateZ(0)', // 하드웨어 가속으로 부드러운 동작
              }}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isTyping}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('chat.send')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPage; 